import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { translate as translateI18n } from '../app/i18n';
import { useTranslation } from '../app/LanguageContext';
import { SavingOverlay } from '../components/SavingOverlay';
import ThemeModeToggle from '../components/ThemeModeToggle';
import { getQuickIntakeOrderTypes, submitQuickIntakeOrder } from '../app/api';
import { handleCurrencyInputChange } from '../app/currency';

const ALLOWED_QUICK_INTAKE_LANG = ['pt-BR', 'en-US'];

const OrderQuickIntakePage = () => {
  const { workspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const { translate, setLanguage } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blockingError, setBlockingError] = useState(null);
  const [surfaceError, setSurfaceError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [orderTypes, setOrderTypes] = useState([]);
  const [orderTypeId, setOrderTypeId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [orderValueDisplay, setOrderValueDisplay] = useState('');
  const [orderValueNumeric, setOrderValueNumeric] = useState(null);
  const [observations, setObservations] = useState('');
  const [files, setFiles] = useState([]);

  const previewUrls = useMemo(
    () => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [files]
  );

  useEffect(() => {
    return () => {
      previewUrls.forEach((u) => {
        if (u) URL.revokeObjectURL(u);
      });
    };
  }, [previewUrls]);

  useEffect(() => {
    const load = async () => {
      const qLang = searchParams.get('lang');
      const fromUrl = ALLOWED_QUICK_INTAKE_LANG.includes(qLang) ? qLang : null;
      const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
      const fromNav = nav.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en-US';
      const lang = fromUrl || fromNav;
      await setLanguage(lang, false);
      if (!workspaceId) {
        setBlockingError(translateI18n('quick_intake.invalid_link') || 'Link inválido');
        setLoading(false);
        return;
      }
      try {
        const res = await getQuickIntakeOrderTypes(workspaceId);
        const list = Array.isArray(res?.data) ? res.data : res;
        const types = Array.isArray(list) ? list : [];
        setOrderTypes(types);
        if (types.length === 0) {
          setBlockingError(translateI18n('quick_intake.no_order_types') || '');
        }
      } catch (err) {
        setBlockingError(err.message || (translateI18n('quick_intake.unavailable') || ''));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, setLanguage, searchParams]);

  const orderValueOk =
    orderValueNumeric != null && Number.isFinite(orderValueNumeric) && orderValueNumeric > 0;

  const handleOrderValueChange = (e) => {
    handleCurrencyInputChange(e, (payload) => {
      if (payload === '') {
        setOrderValueDisplay('');
        setOrderValueNumeric(null);
        return;
      }
      setOrderValueDisplay(payload.target.value);
      setOrderValueNumeric(payload.target.numericValue);
    });
  };

  const handleFilesChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!workspaceId || !orderValueOk) return;
    setSubmitting(true);
    setSurfaceError(null);
    try {
      const payload = {
        order_type_id: orderTypeId,
        delivery_date: deliveryDate,
        observations: observations.trim(),
        order_value: orderValueNumeric,
      };
      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      files.forEach((f) => formData.append('file', f));
      await submitQuickIntakeOrder(workspaceId, formData);
      setSuccess(true);
    } catch (err) {
      setSurfaceError(err.message || (translate('quick_intake.submit_error') || 'Erro'));
    } finally {
      setSubmitting(false);
    }
  };

  const helpItems = useMemo(
    () => [
      { primary: translate('quick_intake.order_type'), secondary: translate('quick_intake.help_order_type') },
      {
        primary: `${translate('quick_intake.delivery_date')} + ${translate('quick_intake.order_value')}`,
        secondary: translate('quick_intake.help_delivery_value'),
      },
      { primary: translate('quick_intake.observations'), secondary: translate('quick_intake.help_observations') },
      { primary: translate('quick_intake.reference_files'), secondary: translate('quick_intake.help_files') },
    ],
    [translate]
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (blockingError) {
    return (
      <Box sx={{ minHeight: '100vh', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 3, maxWidth: 480, width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            {translate('quick_intake.page_title')}
          </Typography>
          <Alert severity="error">{blockingError}</Alert>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 3, maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {translate('quick_intake.success')}
          </Typography>
          <Typography color="text.secondary">{translate('quick_intake.success_message')}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 3, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <SavingOverlay open={submitting} />
      <Box sx={{ position: 'fixed', top: 12, right: 12, zIndex: 10, display: 'flex', gap: 1, alignItems: 'center' }}>
        <ThemeModeToggle />
      </Box>
      <Drawer
        anchor="right"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 300 },
            maxWidth: 300,
          },
        }}
      >
        <Box sx={{ position: 'relative', p: 2, pt: 2.5 }}>
          <IconButton
            size="small"
            onClick={() => setHelpOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-label={translate('quick_intake.help_close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" fontWeight={600} sx={{ pr: 4, mb: 1 }} id="quick-intake-help-title">
            {translate('quick_intake.help_title')}
          </Typography>
          <List dense disablePadding>
            {helpItems.map((item, i) => (
              <React.Fragment key={item.primary}>
                {i > 0 ? <Divider component="li" variant="inset" sx={{ ml: 0, my: 0.75 }} /> : null}
                <ListItem alignItems="flex-start" disableGutters sx={{ py: 0.35 }}>
                  <ListItemText
                    primary={item.primary}
                    secondary={item.secondary}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 600, display: 'block', mb: 0.25 }}
                    secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.75rem', lineHeight: 1.35 } }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          <Button size="small" fullWidth variant="text" onClick={() => setHelpOpen(false)} sx={{ mt: 1.5 }}>
            {translate('quick_intake.help_close')}
          </Button>
        </Box>
      </Drawer>
      <Paper sx={{ p: 2.5, maxWidth: 560, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Typography variant="h5" component="h1" fontWeight={600} sx={{ fontSize: { xs: '1.2rem', sm: '1.35rem' }, lineHeight: 1.3, pr: 0.5 }}>
            {translate('quick_intake.page_title')}
          </Typography>
          <Tooltip title={translate('quick_intake.help_icon_aria')}>
            <IconButton
              size="small"
              edge="end"
              onClick={() => setHelpOpen(true)}
              aria-label={translate('quick_intake.help_icon_aria')}
              sx={{ color: 'text.secondary', mt: -0.25 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {surfaceError ? (
          <Alert severity="error" sx={{ mb: 1.5 }} dense>
            {surfaceError}
          </Alert>
        ) : null}
        <TextField
          fullWidth
          select
          required
          size="small"
          label={translate('quick_intake.order_type')}
          value={orderTypeId}
          onChange={(e) => setOrderTypeId(e.target.value)}
          sx={{ mb: 1.5 }}
        >
          <MenuItem value="">
            <em>{translate('public_form.select')}</em>
          </MenuItem>
          {orderTypes.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 1.5,
            alignItems: { sm: 'flex-start' },
          }}
        >
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label={translate('quick_intake.delivery_date')}
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <TextField
            fullWidth
            required
            size="small"
            label={translate('quick_intake.order_value')}
            value={orderValueDisplay}
            onChange={handleOrderValueChange}
            error={orderValueDisplay !== '' && !orderValueOk}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>
        <TextField
          fullWidth
          size="small"
          label={translate('quick_intake.observations')}
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
          multiline
          minRows={3}
          sx={{ mb: 1 }}
        />
        <Button
          variant="outlined"
          component="label"
          size="small"
          startIcon={<AttachFileIcon sx={{ fontSize: 18 }} />}
          sx={{ mb: files.length ? 0.75 : 0, py: 0.35, fontSize: '0.8rem', alignSelf: 'flex-start' }}
        >
          {translate('quick_intake.reference_files')}
          <input type="file" hidden multiple onChange={handleFilesChange} />
        </Button>
        {files.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, mt: 0.75 }}>
            {files.map((f, idx) => (
              <Box
                key={`${f.name}-${idx}-${f.lastModified}`}
                sx={{
                  position: 'relative',
                  width: 72,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.75,
                  overflow: 'hidden',
                  bgcolor: 'action.hover',
                }}
              >
                <IconButton
                  size="small"
                  aria-label={translate('quick_intake.remove_file')}
                  onClick={() => removeFileAt(idx)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    zIndex: 1,
                    p: 0.25,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
                {previewUrls[idx] ? (
                  <Box
                    component="img"
                    src={previewUrls[idx]}
                    alt=""
                    sx={{ width: '100%', height: 52, objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 52,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InsertDriveFileIcon color="action" sx={{ fontSize: 22 }} />
                  </Box>
                )}
                <Typography
                  variant="caption"
                  display="block"
                  px={0.35}
                  pb={0.25}
                  noWrap
                  title={f.name}
                  sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                >
                  {f.name}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
        <Button
          variant="contained"
          fullWidth
          size="medium"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          disabled={submitting || !orderTypeId || !deliveryDate || !orderValueOk}
          onClick={handleSubmit}
        >
          {translate('quick_intake.submit')}
        </Button>
      </Paper>
    </Box>
  );
};

export default OrderQuickIntakePage;
